/**
 * NudgeDigestPreview (Iteration 44c) — peek at the next Monday nudge email
 * that this admin would receive, and tweak the intro/footer copy before it
 * ships. Nothing is actually sent — the endpoint renders the same HTML the
 * cron would render, applies overrides, and returns it.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, RefreshCw, Loader2, AlertCircle } from "lucide-react";

export default function NudgeDigestPreview() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [intro, setIntro] = useState("");
  const [footer, setFooter] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.post("/workspace/nudge-digest/preview", {
        intro_override: intro || null,
        footer_override: footer || null,
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="nudge-preview-page">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">Monday digest</p>
          <h1 className="font-display text-3xl mt-1">Preview your Monday nudge email</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Tweak the intro and footer, refresh the preview, and iterate before it ships automatically on Monday 09:13 UTC. Nothing is sent from this page.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground">
          <Mail className="w-3.5 h-3.5" /> {data?.next_send_at_utc || "Not scheduled"}
        </div>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Controls */}
        <aside className="lg:col-span-1 space-y-4">
          <div className="rounded-lg border border-border/60 p-4">
            <p className="eyebrow mb-3">Copy overrides</p>
            <label className="text-xs text-muted-foreground">Custom intro (replaces "Hi Name —")</label>
            <Input value={intro} onChange={(e) => setIntro(e.target.value)}
              placeholder='e.g. "Hey team,"'
              data-testid="nudge-preview-intro" className="mt-1 mb-4" />
            <label className="text-xs text-muted-foreground">Footer note</label>
            <Input value={footer} onChange={(e) => setFooter(e.target.value)}
              placeholder='e.g. "Sponsored by the Ops team"'
              data-testid="nudge-preview-footer" className="mt-1 mb-4" />
            <Button onClick={load} disabled={busy} className="w-full rounded-full" data-testid="nudge-preview-refresh">
              {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Rendering…</>
                    : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh preview</>}
            </Button>
          </div>
          {data && (
            <div className="rounded-lg border border-border/60 p-4 text-xs text-muted-foreground space-y-1">
              <p><b className="text-foreground">Subject:</b> {data.subject}</p>
              <p><b className="text-foreground">Personal nudges:</b> {data.personal_count}</p>
              <p><b className="text-foreground">Team nudges:</b> {data.team_count}</p>
              {!data.would_send && (
                <p className="mt-3 flex items-start gap-2 text-amber-500">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
                  With zero nudges triggered you'd be skipped on Monday — that's a good thing.
                </p>
              )}
            </div>
          )}
        </aside>

        {/* Rendered preview iframe */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-border/60 overflow-hidden bg-white" data-testid="nudge-preview-frame-wrapper">
            {data ? (
              <iframe
                title="Nudge digest preview"
                srcDoc={data.html}
                data-testid="nudge-preview-frame"
                sandbox=""
                className="w-full"
                style={{ height: 700, border: 0, background: "#09090b" }}
              />
            ) : (
              <div className="h-[500px] flex items-center justify-center text-sm text-muted-foreground">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Rendering…"}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
